import type {
  Article,
  CreateArticlePayload,
  CreateTrainingPayload,
  CreateVideoPayload,
  GetArticlesPayload,
  GetTrainingsPayload,
  GetVideosPayload,
  SearchArticlesPayload,
  SearchTrainingsPayload,
  SearchVideosPayload,
  Training,
  UpdateArticlePayload,
  UpdateTrainingPayload,
  UpdateVideoPayload,
  Video,
} from '../../types/index.js';
import type { ApiTagType } from '../base-api.js';
import { baseApi } from '../base-api.js';

/**
 * Normalized shape for every paginated list/search endpoint below — the wire envelope is
 * `{ articles/videos/trainings, has_more }`; `transformResponse` renames it once per endpoint so
 * consumers always destructure `{ items, hasMore }` regardless of domain.
 */
type PaginatedResponse<T> = { items: T[]; hasMore: boolean };

const toPaginated = <T extends { id: string }, K extends string>(
  response: Record<K, T[]> & { has_more: boolean },
  itemsKey: K,
): PaginatedResponse<T> => ({ items: response[itemsKey], hasMore: response.has_more });

const listTags = <T extends { id: string }>(type: ApiTagType, result: PaginatedResponse<T> | undefined) =>
  result
    ? [...result.items.map(({ id }) => ({ type, id })), { type, id: 'LIST' as const }]
    : [{ type, id: 'LIST' as const }];

/**
 * Mirrors `apps/server/src/modules/{article,video,training}` exactly (all three controllers
 * share the same list/search/get-by-id/create/update/delete shape, image upload included).
 * Mobile details screens consume `getXById`; web's `article-details-page.tsx` (and siblings)
 * still resolve the item client-side from the already-fetched list query.
 */
export const mediaApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getArticles: builder.query<PaginatedResponse<Article>, GetArticlesPayload>({
      query: (payload) => ({ path: ['articles'], method: 'get', query: payload }),
      transformResponse: (response: { articles: Article[]; has_more: boolean }) => toPaginated(response, 'articles'),
      providesTags: (result) => listTags('Article', result),
    }),
    getAdminArticles: builder.query<PaginatedResponse<Article>, GetArticlesPayload>({
      query: (payload) => ({ path: ['articles', 'admin'], method: 'get', query: payload }),
      transformResponse: (response: { articles: Article[]; has_more: boolean }) => toPaginated(response, 'articles'),
      providesTags: (result) => listTags('Article', result),
    }),
    searchArticles: builder.query<PaginatedResponse<Article>, SearchArticlesPayload>({
      query: (payload) => ({ path: ['articles', 'search'], method: 'get', query: payload }),
      transformResponse: (response: { articles: Article[]; has_more: boolean }) => toPaginated(response, 'articles'),
      providesTags: (result) => listTags('Article', result),
    }),
    getArticleById: builder.query<Article, string>({
      query: (id) => ({ path: ['articles', { param: 'article_id', value: id }], method: 'get' }),
      transformResponse: (response: { article: Article }) => response.article,
      providesTags: (_result, _error, id) => [{ type: 'Article', id }],
    }),
    createArticle: builder.mutation<Article, CreateArticlePayload>({
      query: (body) => ({ path: ['articles'], method: 'post', body }),
      transformResponse: (response: { article: Article; message: string }) => response.article,
      invalidatesTags: [{ type: 'Article', id: 'LIST' }],
    }),
    updateArticle: builder.mutation<Article, { id: string; payload: UpdateArticlePayload }>({
      query: ({ id, payload }) => ({
        path: ['articles', { param: 'article_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: { article: Article; message: string }) => response.article,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Article', id },
        { type: 'Article', id: 'LIST' },
      ],
    }),
    deleteArticle: builder.mutation<void, string>({
      query: (id) => ({ path: ['articles', { param: 'article_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Article', id },
        { type: 'Article', id: 'LIST' },
      ],
    }),

    getVideos: builder.query<PaginatedResponse<Video>, GetVideosPayload>({
      query: (payload) => ({ path: ['videos'], method: 'get', query: payload }),
      transformResponse: (response: { videos: Video[]; has_more: boolean }) => toPaginated(response, 'videos'),
      providesTags: (result) => listTags('Video', result),
    }),
    searchVideos: builder.query<PaginatedResponse<Video>, SearchVideosPayload>({
      query: (payload) => ({ path: ['videos', 'search'], method: 'get', query: payload }),
      transformResponse: (response: { videos: Video[]; has_more: boolean }) => toPaginated(response, 'videos'),
      providesTags: (result) => listTags('Video', result),
    }),
    getVideoById: builder.query<Video, string>({
      query: (id) => ({ path: ['videos', { param: 'video_id', value: id }], method: 'get' }),
      transformResponse: (response: { video: Video }) => response.video,
      providesTags: (_result, _error, id) => [{ type: 'Video', id }],
    }),
    createVideo: builder.mutation<Video, CreateVideoPayload>({
      query: (body) => ({ path: ['videos'], method: 'post', body }),
      transformResponse: (response: { video: Video; message: string }) => response.video,
      invalidatesTags: [{ type: 'Video', id: 'LIST' }],
    }),
    updateVideo: builder.mutation<Video, { id: string; payload: UpdateVideoPayload }>({
      query: ({ id, payload }) => ({
        path: ['videos', { param: 'video_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: { video: Video; message: string }) => response.video,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Video', id },
        { type: 'Video', id: 'LIST' },
      ],
    }),
    deleteVideo: builder.mutation<void, string>({
      query: (id) => ({ path: ['videos', { param: 'video_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Video', id },
        { type: 'Video', id: 'LIST' },
      ],
    }),

    getTrainings: builder.query<PaginatedResponse<Training>, GetTrainingsPayload>({
      query: (payload) => ({ path: ['trainings'], method: 'get', query: payload }),
      transformResponse: (response: { trainings: Training[]; has_more: boolean }) => toPaginated(response, 'trainings'),
      providesTags: (result) => listTags('Training', result),
    }),
    searchTrainings: builder.query<PaginatedResponse<Training>, SearchTrainingsPayload>({
      query: (payload) => ({ path: ['trainings', 'search'], method: 'get', query: payload }),
      transformResponse: (response: { trainings: Training[]; has_more: boolean }) => toPaginated(response, 'trainings'),
      providesTags: (result) => listTags('Training', result),
    }),
    getTrainingById: builder.query<Training, string>({
      query: (id) => ({ path: ['trainings', { param: 'training_id', value: id }], method: 'get' }),
      transformResponse: (response: { training: Training }) => response.training,
      providesTags: (_result, _error, id) => [{ type: 'Training', id }],
    }),
    createTraining: builder.mutation<Training, CreateTrainingPayload>({
      query: (body) => ({ path: ['trainings'], method: 'post', body }),
      transformResponse: (response: { training: Training; message: string }) => response.training,
      invalidatesTags: [{ type: 'Training', id: 'LIST' }],
    }),
    updateTraining: builder.mutation<Training, { id: string; payload: UpdateTrainingPayload }>({
      query: ({ id, payload }) => ({
        path: ['trainings', { param: 'training_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: { training: Training; message: string }) => response.training,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Training', id },
        { type: 'Training', id: 'LIST' },
      ],
    }),
    deleteTraining: builder.mutation<void, string>({
      query: (id) => ({ path: ['trainings', { param: 'training_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Training', id },
        { type: 'Training', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetArticlesQuery,
  useGetAdminArticlesQuery,
  useSearchArticlesQuery,
  useGetArticleByIdQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useGetVideosQuery,
  useSearchVideosQuery,
  useGetVideoByIdQuery,
  useCreateVideoMutation,
  useUpdateVideoMutation,
  useDeleteVideoMutation,
  useGetTrainingsQuery,
  useSearchTrainingsQuery,
  useGetTrainingByIdQuery,
  useCreateTrainingMutation,
  useUpdateTrainingMutation,
  useDeleteTrainingMutation,
} = mediaApi;
